import React, {
  useMemo,
  useState,
  useRef,
  useCallback,
  useEffect,
} from "react";
import { useSelector } from "react-redux";
import { globalChartColor } from "../../../redux/constants/enums";
import { newColorShadeInHex6 } from "../../../redux/constants";
import "./GoogleMultiLayerMap.scss";
import {
  defaultMapStyle,
  getStaticMapStyleParams,
} from "./MapFeatureViewStyles";
import { Tooltip } from "antd";

const GoogleStaticMultiLayerMap = ({
  mapData,
  preview,
  createdWidget,
  PdfDownloadStatus,
  noteExist,
  insightsExist,
  widgetDiscription,
  widget_id,
  language,
}) => {
  const [breakdownLegends, setBreakdownLegends] = useState(null);
  const [thresholdLegends, setThresholdLegends] = useState(null);
  // For legend container height (if legends are rendered below the map)
  const [legendContainerHeightInPx, setLegendContainerHeightInPx] =
    useState("0px");
  const legendContainerRef = useRef(null);

  const updateSize = useCallback(() => {
    if (legendContainerRef?.current) {
      const height = legendContainerRef?.current?.clientHeight || 0;
      setLegendContainerHeightInPx(`${height}px`);
    }
  }, [legendContainerRef, legendContainerRef?.current]);

  useEffect(() => {
    const resizeObserver = new ResizeObserver(() => {
      requestAnimationFrame(updateSize);
    });
    if (legendContainerRef.current) {
      resizeObserver.observe(legendContainerRef.current);
    }
    return () => {
      resizeObserver.disconnect();
    };
  }, [updateSize]);

  // Format the map data similar to your MapContainer component
  const locationsData = useMemo(() => {
    let rawMapData = mapData?.[0]?.latitude
      ? mapData
      : mapData?.[0]?.data?.[0]?.latitude
      ? mapData?.[0]?.data
      : mapData?.data?.[0]?.latitude
      ? mapData?.data
      : [];
    let rawBreakdownData = mapData?.y
      ? mapData?.y
      : mapData?.data?.y
      ? mapData?.data?.y
      : mapData?.[0]?.y
      ? mapData?.[0]?.y
      : [];

    let selectedColorArray = globalChartColor;
    // For calculating breakdownLegend
    // for location breakdown data
    let jBreakdown = 0;
    let noOfIterationInColorsBreakdown = 0;
    let lengthOfColors = selectedColorArray?.length;
    rawBreakdownData = Array.isArray(rawBreakdownData)
      ? rawBreakdownData?.map((breakBY, breakIndex) => {
          let breakdownColor;
          if (
            breakBY?.color !== undefined &&
            breakBY?.color !== "#000000" &&
            breakBY?.color !== null
          ) {
            breakdownColor = breakBY?.color;
          } else {
            if (breakIndex >= lengthOfColors) {
              noOfIterationInColorsBreakdown = parseInt(
                breakIndex / lengthOfColors
              );
              let rem = parseInt(breakIndex % lengthOfColors);
              breakdownColor =
                noOfIterationInColorsBreakdown > 0
                  ? newColorShadeInHex6(
                      selectedColorArray[rem],
                      parseInt(noOfIterationInColorsBreakdown * 5)
                    )
                  : selectedColorArray[breakIndex];
            } else {
              breakdownColor = selectedColorArray[breakIndex];
            }
          }
          return { ...breakBY, color: breakdownColor };
        })
      : rawBreakdownData;
    if (rawBreakdownData) setBreakdownLegends(rawBreakdownData);
    // For calculating thresholdLegend
    let thresholdLegends = mapData?.threshold_range
      ? mapData?.threshold_range
      : mapData?.data?.threshold_range
      ? mapData?.data?.threshold_range
      : mapData?.[0]?.threshold_range
      ? mapData?.[0]?.threshold_range
      : null;

    if (thresholdLegends) setThresholdLegends(thresholdLegends);

    // for location color
    let j = 0;
    let noOfIterationInColors = 0;
    noOfIterationInColorsBreakdown = 0;
    lengthOfColors = selectedColorArray?.length;
    if (Array.isArray(rawMapData)) {
      const updatedMapData = rawMapData?.map((loc, locIndex) => {
        let locationColor;
        if (
          loc?.color !== undefined &&
          loc?.color !== "#000000" &&
          loc?.color !== null
        ) {
          locationColor = loc?.color;
        } else {
          if (j >= lengthOfColors) {
            noOfIterationInColors = parseInt(j / lengthOfColors);
            let rem = parseInt(j % lengthOfColors);
            locationColor =
              noOfIterationInColors > 0
                ? newColorShadeInHex6(
                    selectedColorArray[rem],
                    parseInt(noOfIterationInColors * 5)
                  )
                : selectedColorArray[j];
          } else {
            locationColor = selectedColorArray[j];
          }
          j++;
        }

        // For Breakdown Color
        let breakdownObj = Array.isArray(rawBreakdownData)
          ? rawBreakdownData?.find(
              (breakBY) => breakBY?.name === loc?.breakdown_val
            )
          : null;
        let breakdownColor = breakdownObj?.color
          ? breakdownObj?.color
          : loc?.breakdown_color;

        return {
          id: `${widget_id}_LocationID_${loc?.id}_Index_${locIndex}`,
          name: loc?.name,
          coordinates: { lat: loc?.latitude, lng: loc?.longitude },
          current_metric: loc?.current_metric,
          previous_metric: loc?.previous_metric,
          size: loc?.size,
          previous_size: loc?.previous_size,
          color: locationColor,
          breakdown_val: loc?.breakdown_val,
          breakdown_color: breakdownColor,
        };
      });
      return updatedMapData;
    } else {
      return [];
    }
  }, [mapData, widget_id]);

  // Determine center using first location, or default to Mumbai
  const center = useMemo(() => {
    return locationsData && locationsData?.length
      ? `${locationsData[0]?.coordinates?.lat},${locationsData[0]?.coordinates?.lng}`
      : "19.07609,72.877426";
  }, [locationsData]);
  // Compute the container style based on preview, createdWidget, PdfDownloadStatus, etc.
  const containerStyle = useMemo(() => {
    return {
      position: "relative",
      marginLeft: preview || createdWidget ? "" : "-1.4rem",
      width: preview ? "auto" : "100%",
      height: preview
        ? `calc(100vh - 19.2rem - ${legendContainerHeightInPx})`
        : PdfDownloadStatus
        ? noteExist
          ? widgetDiscription && widgetDiscription?.length
            ? widgetDiscription?.length > 130
              ? `calc(46rem - ${legendContainerHeightInPx})`
              : `calc(48rem - ${legendContainerHeightInPx})`
            : `calc(50rem - ${legendContainerHeightInPx})`
          : widgetDiscription && widgetDiscription?.length
          ? widgetDiscription?.length > 130
            ? `calc(64.5rem - ${legendContainerHeightInPx})`
            : `calc(66.5rem - ${legendContainerHeightInPx})`
          : `calc(68.5rem - ${legendContainerHeightInPx})`
        : createdWidget
        ? `calc(100vh - 16.5rem - ${legendContainerHeightInPx})`
        : `calc(70% - ${legendContainerHeightInPx})`,
    };
  }, [
    preview,
    createdWidget,
    PdfDownloadStatus,
    noteExist,
    widgetDiscription,
    legendContainerHeightInPx,
  ]);
  // console.log(
  //   "mapData : ",
  //   mapData,
  //   "locationsData : ",
  //   locationsData,
  //   "center : ",
  //   center,
  //   "breakdownLegends : ",
  //   breakdownLegends,
  //   "thresholdLegends : ",
  //   thresholdLegends,
  //   "containerStyle : ",
  //   containerStyle
  // );
  const zoom = 5;

  // Compute image size – adjust these values as needed.
  const width = window.innerWidth;
  const height = window.innerHeight;
  const sizeParam = `${width}x${height}`;

  // Build markers query string for the Static Maps API.
  // Note: Static Maps markers allow a simple set of customizations (color, label)
  const markersParam = locationsData?.reduce((acc, loc, index) => {
    // Use the first letter of the location name as the label (only one character allowed)
    const label = loc?.name ? loc?.name?.[0]?.toUpperCase() : "L";
    // For static maps, custom colors need to be in the form '0x' + hex (without the "#")
    const markerColor =
      loc?.color && loc?.color?.startsWith("#")
        ? "0x" + loc?.color?.slice(1)
        : "red";
    return (
      acc +
      `&markers=color:${markerColor}|label:${label}|${loc?.coordinates?.lat},${loc?.coordinates?.lng}`
    );
  }, "");
  // Get style parameters from your style array.
  const styleParams = getStaticMapStyleParams(defaultMapStyle);
  const apiKey = "AIzaSyDGqipm4Lsoq2WkGQDoH1_Iu6eXPjiuE5A";
  // removing &zoom=${zoom} from the URL as it is calculated by the API
  const staticMapUrl = `https://maps.googleapis.com/maps/api/staticmap?center=${center}&size=${sizeParam}&maptype=roadmap&${styleParams}${markersParam}&key=${apiKey}`;

  return (
    <div
      style={{
        height: `calc(${containerStyle?.height} + ${legendContainerHeightInPx})`,
      }}
      className={`StaticGoogleMultiLayerMap_${widget_id}`}
    >
      <div
        className="StaticGoogleMultiLayerMap_Container"
        style={{ width: "100%", height: containerStyle?.height }}
      >
        <img
          src={staticMapUrl}
          alt="Static Google Map"
          style={{ width: "100%", height: "100%" }}
        />
      </div>
      {/* Render legends if available */}
      {breakdownLegends?.length || thresholdLegends ? (
        <div ref={legendContainerRef} className="GoogleMultiLayerMap_Legends">
          {Array.isArray(breakdownLegends) ? (
            <div className="GoogleMultiLayerMap_Legends--row">
              {breakdownLegends?.map((legend, index) => {
                if (index < 10) {
                  return (
                    <span className="map__legend">
                      <span
                        className="map__legend--borderColor"
                        style={{ borderColor: legend?.color }}
                      ></span>
                      <span className="map__legend--name">{legend?.name}</span>
                    </span>
                  );
                } else {
                  return "";
                }
              })}
              {breakdownLegends?.length > 10 ? (
                <Tooltip
                  overlayStyle={{
                    minWidth: "15rem",
                  }}
                  overlayInnerStyle={{
                    padding: "0.8rem",
                    borderRadius: "0.8rem",
                  }}
                  overlayClassName="GoogleMultiLayerMap__Tooltip"
                  title={
                    <div className="GoogleMultiLayerMap_Legends--tooltip">
                      {breakdownLegends?.map((legend, index) => {
                        if (index >= 10) {
                          return (
                            <span className="map__legend">
                              <span
                                className="map__legend--borderColor"
                                style={{ borderColor: legend?.color }}
                              ></span>
                              <span className="map__legend--name">
                                {legend?.name}
                              </span>
                            </span>
                          );
                        } else {
                          return "";
                        }
                      })}
                    </div>
                  }
                  placement="top"
                >
                  <span className="map__legend--count">
                    <span className="map__legend--countName">
                      +{breakdownLegends?.length - 10}
                    </span>
                  </span>
                </Tooltip>
              ) : (
                ""
              )}
            </div>
          ) : (
            ""
          )}
          {thresholdLegends?.low ? (
            <div className="GoogleMultiLayerMap_Legends--row">
              {" "}
              <span className="map__legend">
                <span
                  className="map__legend--color"
                  style={{ backgroundColor: thresholdLegends?.low?.color }}
                ></span>
                <span className="map__legend--name">
                  {thresholdLegends?.low?.name}
                </span>
              </span>
              <span className="map__legend">
                <span
                  className="map__legend--color"
                  style={{ backgroundColor: thresholdLegends?.medium?.color }}
                ></span>
                <span className="map__legend--name">
                  {thresholdLegends?.medium?.name}
                </span>
              </span>
              <span className="map__legend">
                <span
                  className="map__legend--color"
                  style={{ backgroundColor: thresholdLegends?.high?.color }}
                ></span>
                <span className="map__legend--name">
                  {thresholdLegends?.high?.name}
                </span>
              </span>
            </div>
          ) : (
            ""
          )}
        </div>
      ) : (
        ""
      )}
    </div>
  );
};

export default GoogleStaticMultiLayerMap;
